import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { readApiError } from "_shared/_errors/api-error";
import { api } from "_shared/_stores/api";
import AccessRequestSchema, { type AccessRequestJSON } from "permissions/access-requests/_schemas/access-request-schema";
import type { RequestableAccess } from "permissions/access-requests/requestable-access";
import { permissionListTag, sessionPermissionTag } from "permissions/_api/permissions-api-service";

/** The problem codes `AccessRequestsController` answers with, beside its 403 and 404. */
export const AccessRequestProblem = {
  /** 409 on a request: the caller already holds the access. */
  AlreadyHeld: "access_request_already_held",
  /** 409 on a request: nobody in the caller's organization may grant it. */
  NoGrantor: "access_request_no_grantor",
  /** 409 on a decision: somebody else decided it first. */
  AlreadyDecided: "access_request_already_decided",
} as const;

export type RequestAccessArgs = {
  resource: RequestableAccess;
  /** What the requester was doing, such as "Upload files to Patent filing". At most 200 characters. */
  context: string;
  /** The page they asked from, a path inside the app. */
  returnPath?: string;
};

export type DecideAccessRequestArgs = {
  accessRequestId: string;
  /** A word for the requester, sent with the decision. */
  note?: string;
};

/** The longest context the API keeps (`AccessRequest.ContextMaxLength`). */
export const ACCESS_REQUEST_CONTEXT_MAX_LENGTH = 200;
/** The longest page path the API keeps (`AccessRequest.ReturnPathMaxLength`). A longer one is left out. */
export const ACCESS_REQUEST_RETURN_PATH_MAX_LENGTH = 300;
/** The longest note a decision may carry (`AccessRequest.NoteMaxLength`). */
export const ACCESS_REQUEST_NOTE_MAX_LENGTH = 500;

export const myAccessRequestsTag = { type: "AccessRequest" as const, id: "MINE" };
export const organizationAccessRequestsTag = { type: "AccessRequest" as const, id: "LIST" };

/** The problem code of a failed call, when the API sent one it stands behind. */
export function accessRequestProblemCode(error: unknown): string | undefined {
  const result = readApiError(error);
  return result.trusted ? result.code : undefined;
}

function readAccessRequests(response: unknown): AccessRequestJSON[] {
  if (!Array.isArray(response)) throw new Error("Expected the access requests to be an array.");
  return response.map((entry) => AccessRequestSchema.validate(entry));
}

/**
 * A request changes the caller's own list and the organization's. When the API answers that the
 * caller already holds the access, their session's grants are out of date, so those are read again
 * too and the control that offered the request gives way to the one it stood in for.
 */
function requestAccessTags(error: FetchBaseQueryError | undefined) {
  const tags = [myAccessRequestsTag, organizationAccessRequestsTag];
  if (accessRequestProblemCode(error) === AccessRequestProblem.AlreadyHeld) return [...tags, sessionPermissionTag];
  return tags;
}

/**
 * Asking for a permission one lacks, and granting or refusing it (#6596), over
 * `permissions/access-requests`.
 *
 * An approval grants the requester, not the approver, so it invalidates the grant lists the
 * Permissions pages show and leaves the approver's own session grants alone. The requester's browser
 * hears of it through the notification it is sent, and reads its grants again there
 * (`notifications-ably-redux-middleware.ts`).
 */
export const accessRequestsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMyAccessRequests: build.query<AccessRequestJSON[], void>({
      query: () => "permissions/access-requests/mine",
      transformResponse: readAccessRequests,
      providesTags: [myAccessRequestsTag],
    }),
    getAccessRequests: build.query<AccessRequestJSON[], void>({
      query: () => "permissions/access-requests",
      transformResponse: readAccessRequests,
      providesTags: [organizationAccessRequestsTag],
    }),
    requestAccess: build.mutation<AccessRequestJSON, RequestAccessArgs>({
      query: ({ resource, context, returnPath }) => ({
        url: "permissions/access-requests",
        method: "POST",
        body: {
          // JSON text, as a grant command's resource is: the API reads its bodies with Newtonsoft.
          resource: JSON.stringify({
            resourceType: resource.resourceType,
            scope: resource.scope,
            accessLevel: resource.accessLevel,
          }),
          context: context.slice(0, ACCESS_REQUEST_CONTEXT_MAX_LENGTH),
          returnPath:
            returnPath && returnPath.length <= ACCESS_REQUEST_RETURN_PATH_MAX_LENGTH ? returnPath : undefined,
        },
      }),
      transformResponse: (response: unknown) => AccessRequestSchema.validate(response),
      invalidatesTags: (_result, error) => requestAccessTags(error),
    }),
    approveAccessRequest: build.mutation<AccessRequestJSON, DecideAccessRequestArgs>({
      query: ({ accessRequestId, note }) => ({
        url: `permissions/access-requests/${accessRequestId}/approve`,
        method: "POST",
        body: { note: note?.trim() || undefined },
      }),
      transformResponse: (response: unknown) => AccessRequestSchema.validate(response),
      invalidatesTags: [organizationAccessRequestsTag, myAccessRequestsTag, permissionListTag],
    }),
    denyAccessRequest: build.mutation<AccessRequestJSON, DecideAccessRequestArgs>({
      query: ({ accessRequestId, note }) => ({
        url: `permissions/access-requests/${accessRequestId}/deny`,
        method: "POST",
        body: { note: note?.trim() || undefined },
      }),
      transformResponse: (response: unknown) => AccessRequestSchema.validate(response),
      invalidatesTags: [organizationAccessRequestsTag, myAccessRequestsTag],
    }),
  }),
});

export const {
  useGetMyAccessRequestsQuery,
  useGetAccessRequestsQuery,
  useRequestAccessMutation,
  useApproveAccessRequestMutation,
  useDenyAccessRequestMutation,
} = accessRequestsApi;
